/**
 * @typedef {AddsAgentOffer & AddsGroupOffer & GetsAgentOffers & GetsGroupOffers & EditsOffer} OfferService
 * @typedef {import('../../shared/OfferForm/OfferFields').default} OfferFields
 * @typedef {import('../../shared/OfferService/Offer').default} OfferPayload
 * @typedef {import('../../shared/OfferService/AddsAgentOffer').default} AddsAgentOffer
 * @typedef {import('../../shared/OfferService/AddsOffer').default} AddsOffer
 * @typedef {import('../../shared/OfferService/AddsGroupOffer').default} AddsGroupOffer
 * @typedef {import('../../shared/OfferService/EditsOffer').default} EditsOffer
 * @typedef {import('../../shared/OfferService/GetsAgentOffers').default} GetsAgentOffers
 * @typedef {import('../../shared/OfferService/GetsGroupOffers').default} GetsGroupOffers
 * @typedef {import('../../shared/OfferService/OffersProps').default} Props
 * @typedef {import('./AddOfferState').default} State
 * @typedef {import('./ImageData')} ImageData
 */

import { AuthContext } from '../../../shared/AuthProvider';
import { file as validateFile } from '../../../shared/validators';
import { hasCapability } from '../../../utils/capabilities';
import { Redirect } from 'react-router-dom';
import { translate } from '../../../App/Internationalization';
import { withOfferService } from '../../../service-container';
import captureError from '../../../utils/captureError';
import categoryList from '@mooveguru/yhh-shared-config/marketplace-categories.json';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import mimeList from '@mooveguru/yhh-shared-config/files/allowed-mimes.json';
import OfferForm from '../../../App/shared/OfferForm/OfferForm';
import paths from '../../../config/local/paths';
import React from 'react';
import { normalizePostalCodeList } from '../../../utils/postalCodes';

/** @extends {React.Component<Props, State>} */
export class AddOffer extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);

		/** @param {State} state */
		this.state = {
			errorMessage: null,
			formComplete: false,
			image: null,
			initialValues: {
				category: Object.keys(categoryList)?.shift() ?? '',
				description: '',
				isActive: '',
				postalCode: '',
				title: '',
				vendorEmail: '',
				vendorName: '',
				vendorPhone: '',
				website: '',
			},
		};

		this._createOffer = this._createOffer.bind(this);
		this._handleFileUploadChange = this._handleFileUploadChange.bind(this);
		this._handleImageSubmit = this._handleImageSubmit.bind(this);
		this._toOfferPayload = this._toOfferPayload.bind(this);
		this._handleSubmit = this._handleSubmit.bind(this);
		this._submit = this._submit.bind(this);
		this._validateUploadedImage = this._validateUploadedImage.bind(this);
	}

	/**
	 * @protected
	 * @param {OfferFields} offer
	 * @param {(offer: OfferFields) => Promise<string | void>} callback
	 * @returns {Promise<boolean>}
	 */
	async _createOffer(offer, callback) {
		let offerId;

		try {
			/*
			 *Callback should create offer, ideally using the OfferService
			 *class and either `addOffer` or `addGroupOffer`
			 */
			offerId = await callback(offer);
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('global.error'),
				formComplete: false,
			});

			return false;
		}

		if (this.state.image && typeof offerId === 'string') {
			await this._handleImageSubmit(offerId);
		}

		this.setState({
			errorMessage: null,
			formComplete: true,
		});

		return true;
	}

	/**
	 * @protected
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	_handleFileUploadChange(event) {
		this.setState({
			image: event.target?.files?.length ? event.target?.files[0] : null,
		});
	}

	/**
	 * @protected
	 * @param {string} offerId
	 * @returns {Promise<void>}
	 */
	async _handleImageSubmit(offerId) {
		if (this.state.image === null) {
			return;
		}

		try {
			await this.props.offerService.addOfferImage(
				this.state.image,
				offerId
			);
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate(
					'admin.pages.offers.image_upload.error'
				),
			});
		}
	}

	/**
	 * @protected
	 * @param {OfferFields} values
	 * @param {(isSubmitting: boolean) => void} [setSubmitting]
	 * @returns {Promise<void>}
	 */
	async _handleSubmit(values, setSubmitting) {
		try {
			this._validateUploadedImage();
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate(
					'admin.pages.offers.image_upload.error'
				),

				formComplete: false,
			});

			return;
		}

		try {
			await this._submit(values);
		} finally {
			setSubmitting?.(false);
		}
	}

	/**
	 * @protected
	 * @param {OfferFields} offer
	 * @returns {OfferPayload}
	 */
	_toOfferPayload(offer) {
		return {
			category: offer.category,
			description: offer.description,
			endDate: offer.endDate,
			id: offer.id,
			image: offer.image,
			isActive: offer.isActive,
			offerImage: '',
			owner: offer.owner,
			postalCode: this.#isLocalMode()
				? normalizePostalCodeList(offer.postalCode)
				: offer.postalCode,
			startDate: offer.startDate,
			title: offer.title,
			vendorEmail: offer.vendorEmail,
			vendorName: offer.vendorName,
			vendorPhone: offer.vendorPhone,
			website: offer.website,
		};
	}

	/**
	 * @protected
	 * @param {OfferFields} offer
	 * @returns {Promise<void>}
	 */
	async _submit(offer) {
		if (this.#isTenantLevelMode()) {
			const payload = this._toOfferPayload(offer);

			await this._createOffer(offer, () =>
				this.props.offerService.addOffer({
					...payload,
					owner: {
						id: null,
						type: 'tenant',
					},
				})
			);

			return;
		}

		if (this.#isLocalMode()) {
			const payload = this._toOfferPayload(offer);

			await this._createOffer(offer, () =>
				this.props.offerService.addOffer({
					...payload,
					owner: {
						id: null,
						type: 'local',
					},
				})
			);

			return;
		}

		if (offer.groupList === undefined) {
			const payload = this._toOfferPayload(offer);

			await this._createOffer(offer, () =>
				this.props.offerService.addOffer(payload)
			);

			return;
		}

		for (const group of offer.groupList) {
			const payload = this._toOfferPayload(offer);
			const created = await this._createOffer(offer, () =>
				this.props.offerService.addGroupOffer(payload, group.id)
			);

			/*
			 *! TODO: This has the ability to create some Offers for Groups and fail for
			 *! others, leaving the users in a "incomplete" state. There is no way
			 *! to reasonably fix this on the front-end. Instead of the route
			 *! `POST groups/:groupId/offers` we need to create a `POST offers`
			 *! route that takes `group_ids` as a parameter and generates the
			 *! appropriate Offers atomically.
			 */
			if (!created) {
				break;
			}
		}
	}

	/**
	 * @protected
	 * @returns {void}
	 * @throws {Error}
	 */
	_validateUploadedImage() {
		if (!this.state.image) {
			return;
		}

		validateFile(this.state.image, mimeList.images, 'image');
	}

	/**
	 * @returns {string}
	 */
	#determineViewRoute() {
		const authContext =
			/** @type {{ roles: readonly import('../../../shared/AuthProvider/Roles').Roles[] }} */ (
				this.context
			);

		if (hasCapability(authContext, 'view_domain')) {
			return paths.app.admin.offers.view;
		}

		return paths.app.admin.offers.groups;
	}

	/**
	 * @returns {boolean}
	 */
	#isTenantLevelMode() {
		return this.props.location?.state?.vendorMode === 'tenant';
	}

	#isLocalMode() {
		return this.props.location?.state?.vendorMode === 'local';
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		const viewRoute = this.#determineViewRoute();
		const isTenantLevelMode = this.#isTenantLevelMode();
		const vendorMode = this.props.location?.state?.vendorMode ?? 'agent';
		const isLocalLevelMode = this.#isLocalMode();
		if (this.state.formComplete) {
			return (
				<Redirect
					to={{
						pathname: viewRoute,
						state: {
							message: translate(
								'admin.pages.offers.success_message'
							),
							vendorMode,
						},
					}}
				/>
			);
		}

		return (
			<React.Fragment>
				<OfferForm
					allowPostalCodeList={isLocalLevelMode}
					cancelPath={viewRoute}
					formSubmitText={translate('global.offers.add')}
					formTitle={translate('global.offers.add_title')}
					hasGroupField={!isTenantLevelMode && !isLocalLevelMode}
					hasImageField={true}
					initialValues={this.state.initialValues}
					onChange={this._handleFileUploadChange}
					onSubmit={this._handleSubmit}
				/>
				<FormErrorMessages messages={this.state.errorMessage} />
			</React.Fragment>
		);
	}
}

AddOffer.contextType = AuthContext;

export default withOfferService(
	/** @type {React.ComponentType<{[key: string]: unknown, offerService: Partial<import('../../shared/OfferService/OffersService').default>}>} */ (
		AddOffer
	)
);
